/**
 * Filtronic Historical Share Price Chart
 */

import Chart from 'chart.js/auto';


/*
 * Vertical hover guide.
 */
const crosshairPlugin = {

    id: 'filtronicCrosshair',

    afterDatasetsDraw(chart) {

        const activePoints = chart.getActiveElements();

        if (!activePoints.length) {
            return;
        }

        const { ctx, chartArea } = chart;

        const x = activePoints[0].element.x;

        ctx.save();

        ctx.beginPath();

        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);

        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(100, 116, 139, 0.35)';

        ctx.setLineDash([4, 4]);

        ctx.stroke();

        ctx.restore();
    }
};


function initSharePriceCharts() {

    const components = document.querySelectorAll(
        '.share-price-chart'
    );

    components.forEach((component) => {

        const canvas = component.querySelector('canvas');

        if (!canvas) {
            return;
        }

        /*
        * Chart colours.
        *
        * Read from the CSS custom properties so the graph
        * stays consistent with the component styling.
        */
        const componentStyles = getComputedStyle(component);

        const chartColors = {
            accent: componentStyles
                .getPropertyValue('--chart-accent')
                .trim() || '#2563eb',

            text: componentStyles
                .getPropertyValue('--chart-muted')
                .trim() || '#667085',

            border: componentStyles
                .getPropertyValue('--chart-border')
                .trim() || '#e2e5e9'
        };

        /*
         * Prevent duplicate initialisation.
         */
        if (Chart.getChart(canvas)) {
            return;
        }

        /*
         * Retrieve historical data from PHP.
         */
        let history = [];

        try {
            history = JSON.parse(
                component.dataset.shareHistory || '[]'
            );
        } catch (error) {
            console.error(
                'Filtronic share chart: invalid historical data.',
                error
            );
            return;
        }

        if (!Array.isArray(history) || !history.length) {
            return;
        }

        /*
         * Convert YYYY-MM-DD into a local date.
         *
         * Avoid new Date('YYYY-MM-DD'), which can introduce
         * timezone-related date shifts.
         */
        function parseDate(dateString) {

            const [year, month, day] = dateString
                .split('-')
                .map(Number);

            return new Date(year, month - 1, day);
        }


        /*
         * Filter prices by the selected period.
         *
         * Use the latest available trading date as
         * the reference, rather than today's date.
         */
        function filterHistory(period) {

            if (period === '5y') {
                return history;
            }

            const latestDate = parseDate(
                history[history.length - 1].date
            );

            const startDate = new Date(latestDate);

            switch (period) {

                case '1m':
                    startDate.setMonth(
                        startDate.getMonth() - 1
                    );
                    break;

                case '3m':
                    startDate.setMonth(
                        startDate.getMonth() - 3
                    );
                    break;

                case '1y':
                    startDate.setFullYear(
                        startDate.getFullYear() - 1
                    );
                    break;

            }

            return history.filter((item) => {
                return parseDate(item.date) >= startDate;
            });
        }


        /*
         * Format dates for labels and tooltips.
         */
        function formatDate(dateString) {

            return parseDate(dateString).toLocaleDateString(
                'en-GB',
                {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                }
            );
        }

        /*
        * Format X-axis labels according to the selected period.
        */
        function formatAxisDate(dateString, period) {

            const date = parseDate(dateString);

            switch (period) {

                case '1m':
                    // 19 Sep, 26 Sep, 3 Oct
                    return date.toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short'
                    });

                case '3m':
                case '1y':
                    // Jul 26, Aug 26, Sep 26
                    return date.toLocaleDateString('en-GB', {
                        month: 'short',
                        year: '2-digit'
                    });

                case '5y':
                    // 2022, 2023, 2024
                    return date.getFullYear().toString();

                default:
                    return formatDate(dateString);
            }
        }


        /*
         * Format share prices in pence.
         */
        function formatPrice(price) {
            return `${Number(price).toFixed(2)}p`;
        }


        /*
         * Initial period.
         */
        let activePeriod = '1m';

        const initialData = filterHistory(activePeriod);


        /*
         * Create the Chart.js instance.
         */
        const chart = new Chart(canvas, {

            type: 'line',

            plugins: [crosshairPlugin],

            data: {

                labels: initialData.map(
                    (item) => item.date
                ),

                datasets: [
                    {
                        label: 'Filtronic Share Price',

                        data: initialData.map(
                            (item) => item.price
                        ),

                        borderColor: chartColors.accent,

                        borderWidth: 2.5,

                        backgroundColor: 'transparent',

                        pointRadius: 0,

                        pointHoverRadius: 5,

                        pointHoverBackgroundColor: chartColors.accent,

                        pointHoverBorderColor: '#ffffff',

                        pointHoverBorderWidth: 2,

                        pointHitRadius: 12,

                        tension: 0.15,

                        fill: false
                    }
                ]
            },

            options: {

                responsive: true,

                maintainAspectRatio: false,

                interaction: {
                    mode: 'index',
                    intersect: false
                },

                plugins: {

                    legend: {
                        display: false
                    },

                    tooltip: {

                        enabled: true,

                        displayColors: false,

                        backgroundColor: '#172033',

                        titleColor: '#ffffff',

                        bodyColor: '#ffffff',

                        padding: 14,

                        cornerRadius: 4,

                        titleFont: {
                            size: 12,
                            weight: 'normal'
                        },

                        bodyFont: {
                            size: 15,
                            weight: 'bold'
                        },

                        callbacks: {

                            title: function (items) {

                                if (!items.length) {
                                    return '';
                                }

                                return formatDate(items[0].label);
                            },

                            label: function (context) {

                                return formatPrice(context.parsed.y);
                            }
                        }
                    }
                },

                scales: {

                    x: {

                        border: {
                            display: false
                        },

                        grid: {
                            display: false
                        },

                        ticks: {

                            color: chartColors.text,

                            font: {
                                size: 12
                            },

                            maxTicksLimit: 7,

                            maxRotation: 0,

                            padding: 12,

                            callback: function (value, index, ticks) {

                                const dateString = this.getLabelForValue(value);

                                const label = formatAxisDate(
                                    dateString,
                                    activePeriod
                                );

                                /*
                                * For the five-year view, suppress duplicate
                                * year labels.
                                */
                                if (activePeriod === '5y' && index > 0) {

                                    const previousValue = ticks[index - 1].value;

                                    const previousDate = this.getLabelForValue(
                                        previousValue
                                    );

                                    const previousLabel = formatAxisDate(
                                        previousDate,
                                        activePeriod
                                    );

                                    if (label === previousLabel) {
                                        return '';
                                    }
                                }

                                return label;
                            }
                        }
                    },

                    y: {

                        beginAtZero: false,

                        position: 'right',

                        border: {
                            display: false,
                            dash: [4, 4]
                        },

                        grid: {
                            color: chartColors.border,
                            lineWidth: 1
                        },

                        ticks: {

                            color: chartColors.text,

                            font: {
                                size: 12
                            },

                            padding: 12,

                            maxTicksLimit: 6,

                            callback: function (value) {
                                return formatPrice(value);
                            }
                        }
                    }
                }

            }
        });


        /*
         * Handle period buttons.
         */
        const buttons = component.querySelectorAll(
            '.share-price-chart__period'
        );

        buttons.forEach((button) => {

            button.addEventListener('click', () => {

                const period = button.dataset.period;

                if (period === activePeriod) {
                    return;
                }

                activePeriod = period;

                /*
                 * Update button states.
                 */
                buttons.forEach((item) => {

                    const isActive = item === button;

                    item.classList.toggle(
                        'is-active',
                        isActive
                    );

                    item.setAttribute(
                        'aria-pressed',
                        String(isActive)
                    );
                });


                /*
                 * Filter the historical data.
                 */
                const filteredData = filterHistory(period);


                /*
                 * Update the existing chart.
                 *
                 * We don't destroy and recreate it.
                 */
                chart.data.labels = filteredData.map(
                    (item) => item.date
                );

                chart.data.datasets[0].data = filteredData.map(
                    (item) => item.price
                );

                chart.update();

            });

        });

    });

}


/*
 * Initialise when the DOM is ready.
 */
if (document.readyState === 'loading') {

    document.addEventListener(
        'DOMContentLoaded',
        initSharePriceCharts
    );

} else {

    initSharePriceCharts();

}